import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	FormHelperText,
	SelectChangeEvent,
	Alert,
	CircularProgress,
} from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	processFileError,
	processFileRequest,
	processFileSuccess,
} from 'redux/reducers/salesProcessing/reducer';
import { IRequestWithPageSortFilterSearch } from 'redux/types';
import {
	getSalesProcessingProcessingTypes,
	getSalesProcessingFileStatuses,
	getSalesProcessingEmInvoicingFeeTypes,
	getSalesProcessingEmInvoicingFeeTypesLoading,
	getSalesProcessingProcessFileSuccess,
	getSalesProcessingProcessFileError,
	getSalesProcessingProcessFileLoading,
} from 'redux/reducers/salesProcessing/selectors';
import { TProcessingTypes, TSalesProcessingRow } from 'redux/reducers/salesProcessing/types';
import { EFileStatus, EFileStatusId } from '../tableStructure';

enum EProcessingType {
	STANDARD = 'Standard',
	EXPRESS = 'Express',
}

interface TSalesReportProcessingDialogProps {
	open: boolean;
	onClose: () => void;
	reloadBody: IRequestWithPageSortFilterSearch;
	data: TSalesProcessingRow | null;
}

export const SalesReportProcessingDialog: FC<TSalesReportProcessingDialogProps> = ({
	open,
	onClose,
	reloadBody,
	data,
}) => {
	const dispatch = useDispatch();
	const processingTypes = useSelector(getSalesProcessingProcessingTypes);
	const fileStatusesTmp = useSelector(getSalesProcessingFileStatuses);
	const emInvoicingFeeTypes = useSelector(getSalesProcessingEmInvoicingFeeTypes);
	const emInvoicingFeeTypesLoading = useSelector(getSalesProcessingEmInvoicingFeeTypesLoading);
	const isProcessFileError = useSelector(getSalesProcessingProcessFileError);
	const isProcessFileSuccess = useSelector(getSalesProcessingProcessFileSuccess);
	const isProcessFileLoading = useSelector(getSalesProcessingProcessFileLoading);
	const fileStatuses = fileStatusesTmp?.filter(
		status => status.name === 'Failed' || status.name === 'Processed',
	);

	const [comments, setComments] = useState('');
	const [fileStatus, setFileStatus] = useState<EFileStatus | null>(null);
	const [processingType, setProcessingType] = useState<string | null>(null);
	const [processingTypeError, setProcessingTypeError] = useState('');
	const [jobIds, setJobIds] = useState<string | null>(null);
	const [jobIdsError, setJobIdsError] = useState('');
	const [fileStatusError, setFileStatusError] = useState('');
	const [csvFee, setCsvFee] = useState<number | null>(null);
	const [csvFeeError, setCsvFeeError] = useState('');
	const validateForm = (
		processingTypeValidation: string | null,
		fileStatusValidation: EFileStatus | null,
		jobIdsValidation: string | null,
		csvFeeValidation: number | null,
	) => {
		let error = false;
		if (processingTypeValidation === null) {
			setProcessingTypeError('Processing type is required');
			error = true;
		} else setProcessingTypeError('');
		if (
			fileStatusValidation === null ||
			(fileStatusValidation !== EFileStatus.FAILED &&
				fileStatusValidation !== EFileStatus.PROCESSED)
		) {
			setFileStatusError('File status is required');
			error = true;
		} else setFileStatusError('');
		if (
			fileStatusValidation === EFileStatus.PROCESSED &&
			(jobIdsValidation === null || jobIdsValidation === '')
		) {
			setJobIdsError('Job IDs are required');
			error = true;
		} else setJobIdsError('');
		// if (csvFeeValidation === null) {
		// 	setCsvFeeError('CSV fee is required');
		// 	error = true;
		// } else setCsvFeeError('');
		return error;
	};

	const handleProcessingTypeChange = (e: SelectChangeEvent<string>) => {
		setProcessingType(e.target.value as EProcessingType);
		setProcessingTypeError('');
		dispatch(processFileError(''));

		validateForm(e.target.value as EProcessingType, fileStatus, jobIds, csvFee);
	};

	const handleFileStatusChange = (e: SelectChangeEvent<string>) => {
		setFileStatus(e.target.value as EFileStatus);
		setFileStatusError('');
		dispatch(processFileError(''));

		validateForm(processingType, e.target.value as EFileStatus, jobIds, csvFee);
	};

	const handleJobIdsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setJobIds(e.target.value);
		setJobIdsError('');
		dispatch(processFileError(''));

		validateForm(processingType, fileStatus, e.target.value, csvFee);
	};

	const handleCsvFeeChange = (e: SelectChangeEvent<string>) => {
		setCsvFee(Number(e.target.value));
		setCsvFeeError('');
		dispatch(processFileError(''));

		validateForm(processingType, fileStatus, jobIds, Number(e.target.value));
	};

	const handleCommentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setComments(e.target.value);
		dispatch(processFileError(''));
	};

	useEffect(() => {
		if (!open) return;
		setFileStatus(
			null,
			// (data?.fileStatus as EFileStatus) ?? EFileStatus.NEW
		);
		setComments(data?.easComments ?? '');
		setProcessingType(
			processingTypes?.find(type => type.name === data?.processingType)?.name ?? null,
		);
	}, [data, open, processingTypes]);

	const handleEraseForm = () => {
		setComments('');
		setFileStatus(null);
		setProcessingType(null);
		setJobIds(null);
		setCsvFee(null);
		setProcessingTypeError('');
		setJobIdsError('');
		setCsvFeeError('');
	};

	const handleProcessing = () => {
		if (validateForm(processingType, fileStatus, jobIds, csvFee)) return;

		dispatch(
			processFileRequest({
				id: data?.id ?? 0,
				body: {
					easComments: comments,
					fileStatusId: fileStatuses?.find(status => status.name === fileStatus)?.id ?? 0,
					processingType: processingTypes?.find(type => type.name === processingType)?.value ?? 1,
					jobIds,
					csvFee: csvFee ?? null,
				},
				reloadBody,
			}),
		);
		handleEraseForm();
		// onClose();
	};

	const handleClose = () => {
		handleEraseForm();
		onClose();
		dispatch(processFileError(''));
	};

	useEffect(() => {
		if (isProcessFileError) {
			// notificationContainer(processFileError, 'error');
		}
	}, [isProcessFileError]);

	useEffect(() => {
		if (isProcessFileSuccess) {
			handleClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isProcessFileSuccess]);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle
				color="primary"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				Process Sales Report
			</DialogTitle>
			<DialogContent>
				{isProcessFileLoading && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							// backgroundColor: 'rgba(0, 0, 0, 0.5)',
							zIndex: 1000,
							height: '100%',
							width: '100%',
						}}
					>
						<CircularProgress />
					</Box>
				)}
				<Box
					sx={{
						mb: 2,
						p: '10px 0 0 0',
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						opacity: isProcessFileLoading ? 0.5 : 1,
					}}
				>
					<TextField
						label="EAS Comments"
						multiline
						rows={4}
						fullWidth
						value={comments}
						onChange={handleCommentsChange}
						sx={{
							'& .MuiInputBase-input': {
								resize: 'vertical',
							},
						}}
					/>
					<FormControl error={!!fileStatusError}>
						<InputLabel>File Status</InputLabel>
						<Select
							label="File Status"
							placeholder="File Status"
							fullWidth
							value={fileStatus ?? ''}
							onChange={handleFileStatusChange}
						>
							{fileStatuses?.map(status => (
								<MenuItem key={status.id} value={status.name}>
									{status.name}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>{fileStatusError}</FormHelperText>
					</FormControl>
					<FormControl error={!!processingTypeError}>
						<InputLabel>Processing Type</InputLabel>
						<Select
							label="Processing Type"
							placeholder="Processing Type"
							fullWidth
							value={processingType ?? ''}
							onChange={handleProcessingTypeChange}
						>
							{processingTypes?.map(type => (
								<MenuItem key={type.value} value={type.name}>
									{type.name}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>{processingTypeError}</FormHelperText>
					</FormControl>
					<FormControl>
						<InputLabel>CSV Fee</InputLabel>
						<Select
							label="CSV Fee"
							placeholder="CSV Fee"
							fullWidth
							value={csvFee?.toString() ?? ''}
							onChange={handleCsvFeeChange}
						>
							{emInvoicingFeeTypes?.map(type => (
								<MenuItem key={type.id} value={type.id}>
									{type.name}
								</MenuItem>
							))}
						</Select>
						{/* <FormHelperText>{csvFeeError}</FormHelperText> */}
					</FormControl>

					<TextField
						label="Job IDs"
						fullWidth
						value={jobIds}
						onChange={handleJobIdsChange}
						error={!!jobIdsError}
						helperText={jobIdsError}
					/>
					{isProcessFileError && <Alert severity="error">{isProcessFileError}</Alert>}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isProcessFileLoading}>
					Cancel
				</Button>
				<Button onClick={handleProcessing} color="primary" disabled={isProcessFileLoading}>
					Process
				</Button>
			</DialogActions>
		</Dialog>
	);
};
